/**
 * fetch 関連ベース処理
 * From: 1.0.0
 *
 * @packageDocumentation
 */

import merge from 'deepmerge';

import { ErrorInfo } from '../../../application/models/ErrorInfo';
import { ApiResponseSchemaOnFailure } from '../../../application/models/Response';
import { AppError } from '../../models/errors';
import { CommunicationError } from '../../models/errors/communication/CommunicationError';
import { HTTPRequestMethod, isFormData } from '../../utils/ApiUtils';
import { env } from '../../utils/EnvUtils';
import { getToken, updateToken } from '../../utils/KeycloakUtils';
import { defaultErrors, Errors, undefinedErrors } from '../Errors';
import { getFormDataRequestHeaders, getRequestHeaders } from './header';

export type ResponseBodyType =
  | 'JSON'
  | 'FORMDATA'
  | 'TEXT'
  | 'BLOB'
  | 'ARRAYBUFFER';

type QueryParams<REQ, METHOD extends HTTPRequestMethod> = (METHOD extends
  | 'POST'
  | 'PUT'
  | 'PATCH'
  ? { data: REQ }
  : {
      data?: REQ;
    }) & {
  options?: RequestInit;
  responseBodyType?: ResponseBodyType | null;
  withoutAuthentication?: boolean;
};

const buildRequest = <REQ>(
  data?: REQ,
  options?: RequestInit,
  token?: string | null
): RequestInit =>
  isFormData(data)
    ? ({
        ...merge(
          {
            headers:
              data && isFormData(data)
                ? getFormDataRequestHeaders(token)
                : getRequestHeaders(token),
            mode: 'cors',
            credentials: env.REQUEST_CREDENTIALS,
          },
          options || {}
        ),
        body: data,
      } as RequestInit)
    : (merge(
        {
          headers:
            data && isFormData(data)
              ? getFormDataRequestHeaders(token)
              : getRequestHeaders(token),
          body: data
            ? typeof data === 'string'
              ? data
              : JSON.stringify(data)
            : null,
          mode: 'cors',
          credentials: env.REQUEST_CREDENTIALS,
        },
        options || {}
      ) as RequestInit);

const checkResponse = (errors: Errors) => async (res: Response) => {
  // ---- Response OK(satus code:200-299) ---- //
  // Promise で wrap し直して返す
  if (res.ok) return new Promise<Response>((promise) => promise(res));

  // ---- Response NG ---- //
  // Response body の中身を見る
  // Error Response は json 形式のはずだが、json 形式で無かった場合は status code で判定する
  const errorInfo = (await res.json().catch((_e) => {
    if (Object.keys(errors).includes(res.status.toString())) {
      return errors[res.status](res);
    }
    return undefinedErrors(res);
  })) as ErrorInfo | null;

  // FW のエラーレスポンス形式か判定
  const { success: isFrameworkCompliedError } =
    ApiResponseSchemaOnFailure.safeParse(errorInfo);

  // FW 定義エラーだった
  if (isFrameworkCompliedError) {
    if (errorInfo === null) {
      throw new Error(
        `No ErrorInfo: (status code: ${res.status}, status text: ${res.statusText})`
      );
    } else {
      switch (errorInfo.level) {
        case 'FATAL':
        case 'WARNING':
          throw new AppError(
            undefined,
            errorInfo.message,
            undefined,
            errorInfo.level,
            errorInfo.invalidInfos,
            errorInfo.code,
            res.status
          );
        default:
          break;
      }
    }
  }

  // FW 定義エラーではなかった
  if (Object.keys(errors).includes(res.status.toString())) {
    return errors[res.status](res);
  }
  return undefinedErrors(res);
};

export const communicate = (
  url: RequestInfo,
  errors: Errors,
  options?: RequestInit
) => {
  return (
    fetch(url, options)
      // ネットワーク周辺等，リクエスト前のエラー処理
      .catch((e) => {
        throw new Error(e);
      })
      // Response の中身をチェック
      .then<Response | CommunicationError>(checkResponse(errors))
      // 正常レスポンス or エラーで分岐
      .then((resOrError) => {
        if (resOrError instanceof Response && resOrError.ok) {
          return resOrError;
        } else {
          throw resOrError;
        }
      })
  );
};

const commonMethodProc =
  (method: HTTPRequestMethod) =>
  <REQ, RES = Response>(
    url: string,
    {
      data,
      options,
      responseBodyType,
      withoutAuthentication,
    }: QueryParams<REQ, typeof method> = {}
  ): Promise<RES> =>
    communicate(
      url,
      defaultErrors,
      buildRequest(
        data,
        { ...options, method },
        withoutAuthentication ? null : getToken()
      )
    ).then<RES>((res) => {
      switch (responseBodyType) {
        default:
        case 'JSON':
          return res.json();
        case 'FORMDATA':
          return res.formData();
        case 'TEXT':
          return res.text();
        case 'BLOB':
          return res.blob().then((blob) => ({
            file: blob,
            filename: getFileNameFromContentDisposition(
              res.headers.get('Content-Disposition')
            ),
          }));
        case 'ARRAYBUFFER':
          return res.arrayBuffer().then((ab) => ({
            file: ab,
            filename: getFileNameFromContentDisposition(
              res.headers.get('Content-Disposition')
            ),
          }));
        case null:
          return new Promise<void>((resolve) => resolve());
      }
    });

export const commonMethod =
  (method: HTTPRequestMethod) =>
  async <REQ, RES = Response>(
    url: string,
    {
      data,
      options = {},
      responseBodyType = 'JSON',
      withoutAuthentication = false,
    }: QueryParams<REQ, typeof method> = {}
  ): Promise<RES> => {
    console.debug(`${method} ${url} called.`);

    const proc = commonMethodProc(method)(url, {
      data,
      options,
      responseBodyType,
      withoutAuthentication,
    }) as Promise<RES>;

    // 認証不要: そのまま通信本処理を返す
    // 認証必要: token が古ければ更新して通信本処理を返す
    return withoutAuthentication ? proc : updateToken().then(() => proc);
  };

const getFileNameFromContentDisposition = (
  contentDisposition: string | null
) => {
  const disposition = contentDisposition
    ?.split('; ')
    .find((disp) => disp.startsWith('filename'));

  if (disposition) {
    const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
    if (matches !== null && matches[1]) {
      return matches[1].replace(/['"]/g, '') || undefined;
    }
  }

  return undefined;
};
